import type { SymbolId } from './types';

export type CardMotif = 'none' | 'arcane' | 'pyramid' | 'solar';

export interface SymbolCardStyle {
  readonly deepColor: number;
  readonly surfaceColor: number;
  readonly labelColor: number;
  readonly frameColor: number;
  readonly secondaryColor: number;
  readonly cardBackTint: number;
  readonly cardBackAlpha: number;
  readonly auraAlpha: number;
  readonly frameAlpha: number;
  readonly frameWidth: number;
  readonly artWidth: number;
  readonly artHeight: number;
  readonly motif: CardMotif;
}

const LOW_CARD: SymbolCardStyle = {
  deepColor: 0x03070e,
  surfaceColor: 0x101a2b,
  labelColor: 0x02060c,
  frameColor: 0xa98642,
  secondaryColor: 0x596881,
  cardBackTint: 0xc09a5b,
  cardBackAlpha: 0.105,
  auraAlpha: 0.018,
  frameAlpha: 0.44,
  frameWidth: 1.3,
  artWidth: 0,
  artHeight: 0,
  motif: 'none',
};

/**
 * Art direction for the revealed card faces. These profiles deliberately use
 * different hue families so mid, premium, Wild and Scatter remain readable at
 * a glance while the shared card-back watermark keeps the deck coherent.
 */
export const SYMBOL_CARD_STYLES: Readonly<Record<SymbolId, SymbolCardStyle>> = {
  ACE: LOW_CARD,
  KING: { ...LOW_CARD, surfaceColor: 0x111a29, frameColor: 0xb58f43 },
  QUEEN: { ...LOW_CARD, surfaceColor: 0x101827, frameColor: 0x9c7c43 },
  JACK: { ...LOW_CARD, surfaceColor: 0x0e1726, frameColor: 0x876d3d },
  JINZO: {
    deepColor: 0x070611,
    surfaceColor: 0x1b1230,
    labelColor: 0x080610,
    frameColor: 0xc08dff,
    secondaryColor: 0x48bfae,
    cardBackTint: 0xa789be,
    cardBackAlpha: 0.09,
    auraAlpha: 0.085,
    frameAlpha: 0.73,
    frameWidth: 1.8,
    artWidth: 93,
    artHeight: 94,
    motif: 'arcane',
  },
  TW: {
    deepColor: 0x0d0703,
    surfaceColor: 0x28170c,
    labelColor: 0x0d0703,
    frameColor: 0xe5b45d,
    secondaryColor: 0x8e58b9,
    cardBackTint: 0xc79b64,
    cardBackAlpha: 0.095,
    auraAlpha: 0.09,
    frameAlpha: 0.76,
    frameWidth: 1.8,
    artWidth: 96,
    artHeight: 94,
    motif: 'solar',
  },
  BEWD: {
    deepColor: 0x020713,
    surfaceColor: 0x091b31,
    labelColor: 0x020711,
    frameColor: 0x82dbff,
    secondaryColor: 0xb9eaff,
    cardBackTint: 0x87abc0,
    cardBackAlpha: 0.075,
    auraAlpha: 0.1,
    frameAlpha: 0.8,
    frameWidth: 2,
    artWidth: 103,
    artHeight: 99,
    motif: 'arcane',
  },
  DM: {
    deepColor: 0x0d0512,
    surfaceColor: 0x26102b,
    labelColor: 0x0b040f,
    frameColor: 0xff82cf,
    secondaryColor: 0x8f70dd,
    cardBackTint: 0xb285ac,
    cardBackAlpha: 0.078,
    auraAlpha: 0.095,
    frameAlpha: 0.8,
    frameWidth: 2,
    artWidth: 101,
    artHeight: 99,
    motif: 'arcane',
  },
  WILD: {
    deepColor: 0x090501,
    surfaceColor: 0x322006,
    labelColor: 0x0b0601,
    frameColor: 0xffdc72,
    secondaryColor: 0xc5364e,
    cardBackTint: 0xffc75f,
    cardBackAlpha: 0.135,
    auraAlpha: 0.13,
    frameAlpha: 0.94,
    frameWidth: 2.6,
    artWidth: 101,
    artHeight: 98,
    motif: 'pyramid',
  },
  SCATTER: {
    deepColor: 0x080104,
    surfaceColor: 0x310914,
    labelColor: 0x0a0204,
    frameColor: 0xff637d,
    secondaryColor: 0xe5aa4d,
    cardBackTint: 0xd58b7c,
    cardBackAlpha: 0.065,
    auraAlpha: 0.14,
    frameAlpha: 0.9,
    frameWidth: 2.3,
    artWidth: 86,
    artHeight: 84,
    motif: 'solar',
  },
};

export function getSymbolCardStyle(symbolId: SymbolId): SymbolCardStyle {
  return SYMBOL_CARD_STYLES[symbolId];
}
